import { questTitle } from '../data/guide/quests'
import type { QuestClassReq, QuestDropRule, QuestObjective, QuestRule } from '../types/quest'
import type { Spawn, WorldData } from '../types/world'
import type { CharacterClass, ClassId } from '../types/progression'

/** Código de clase de los archivos ⇄ clase de la guía. */
export const classCodeOf = (id: ClassId) => id.toUpperCase()

/** Nombre de la etapa de clase según required_change_up_plus_one (1 inicial, 2 segunda, 3 tercera). */
export function stageName(cls: CharacterClass | undefined, plusOne: number | undefined): string | undefined {
  if (!cls || plusOne === undefined) return undefined
  if (plusOne === 1) return cls.base
  if (plusOne === 2) return cls.second
  if (plusOne === 3) return cls.third
  return undefined
}

const fmt = (n: number) => n.toLocaleString('es-ES')

export type QuestTargetView = { monsterId: number; monsterName: string; mapId?: number; mapName?: string; spawns: Spawn[]; fromRoutes: boolean }

export type QuestDropView = {
  rule: QuestDropRule
  /** "600 de 10.000 (6 %)". Tirada condicional de misión, no probabilidad final por muerte. */
  roll?: string
  /** Condiciones legibles para el jugador. */
  conditions: string[]
  targets: QuestTargetView[]
}

export type QuestItemView = {
  objective: QuestObjective
  itemId: string
  /** "14,23@1" si el catálogo de variantes lo contiene. */
  variantId?: string
  name: string
  nameFromVariant: boolean
  /** Procedencia del nombre mostrado cuando es de presentación (no el generado por el exportador). */
  nameSource?: string
  quantity?: number
  classes: QuestClassReq[]
  drop?: QuestDropView
}

export type QuestKillView = { objective: QuestObjective; monsterId?: number; name: string; resolved: boolean; quantity?: number; maps: { id: number; name: string }[] }

export type QuestStartView = { rule: QuestRule; classes: QuestClassReq[]; prereqTitle?: string }

export type QuestView = {
  questId: number
  title: string
  npcMonsterId?: number
  npcName?: string
  levelMin?: number
  levelMax?: number
  /** Reglas de inicio (estado normal) aplicables a la clase elegida, o todas si no hay clase. */
  starts: QuestStartView[]
  /** Zen requerido. Igual para iniciar desde "normal" o desde "cancelada": se paga al aceptar, no dos veces. */
  zen?: number
  zenStates: string[]
  zenConsistent: boolean
  items: QuestItemView[]
  kills: QuestKillView[]
  /** La clase elegida tiene al menos una regla de inicio. */
  eligible: boolean
}

const appliesTo = (reqs: QuestClassReq[] | undefined, code?: string) => !code || (reqs ?? []).some((r) => r.classCode === code)

/** Variante exacta (item_id + item_level). Nunca fusiona @0 con @1. */
export function variantFor(w: WorldData, itemId: string, level?: number): { id?: string; name: string; fromVariant: boolean; nameSource?: string } {
  if (level !== undefined) {
    const v = w.variants.get(`${itemId}@${level}`) ?? (w.idx.variantsByItem.get(itemId) ?? []).find((x) => x.level === level && x.id !== itemId)
    if (v) return { id: v.id, name: v.name, fromVariant: true, nameSource: v.nameSource }
  }
  // Sin variante en el catálogo: no se inventa "base +nivel" (ese patrón es del exportador, no del cliente).
  const base = w.items.get(itemId)
  return { name: base ? (level ? `${base.name} (variante nivel ${level})` : base.name) : `Ítem ${itemId}${level !== undefined ? ` (nivel ${level})` : ''}`, fromVariant: false }
}

function dropView(w: WorldData, rule: QuestDropRule, variantId?: string): QuestDropView {
  const pairs = new Map<string, QuestTargetView>()
  const add = (monsterId: number | undefined, mapId: number | undefined, fromRoutes: boolean) => {
    if (monsterId === undefined) return
    const k = `${monsterId}|${mapId ?? ''}`
    if (pairs.has(k)) return
    pairs.set(k, {
      monsterId,
      monsterName: w.monsters.get(monsterId)?.name ?? `Monstruo ${monsterId}`,
      mapId,
      mapName: mapId !== undefined ? w.maps.get(mapId)?.name : undefined,
      spawns: (w.idx.spawnsByMonster.get(monsterId) ?? []).filter((s) => mapId === undefined || s.mapId === mapId),
      fromRoutes,
    })
  }
  ;(w.quests.dropTargets.get(rule.id) ?? []).forEach((t) => add(t.monsterId, t.mapId, false))
  // Misma relación en route_targets.csv (rutas kind=quest de esta regla o de la misma variante): se suma sin duplicar.
  w.routes
    .filter((r) => r.kind === 'quest' && (r.questDropRuleId === rule.id || (variantId !== undefined && r.variantId === variantId)))
    .forEach((r) => (w.idx.routeTargetsByRoute.get(r.id) ?? []).forEach((t) => add(t.monsterId, t.mapId, true)))

  const conditions: string[] = []
  if (rule.requiredState !== undefined) conditions.push('Solo con la misión aceptada (en curso).')
  conditions.push('Solo para las clases y evoluciones que pueden hacer esta misión.')
  if (rule.inventoryLessThan !== undefined)
    conditions.push(rule.inventoryLessThan === 1 ? 'Solo si no llevas ya este objeto en el inventario.' : `Solo si llevas menos de ${rule.inventoryLessThan} en el inventario.`)
  if (rule.selector === 'monster_level_range' && rule.monsterLevelMin !== undefined && rule.monsterLevelMax !== undefined)
    conditions.push(`Monstruos de nivel ${rule.monsterLevelMin} a ${rule.monsterLevelMax}.`)
  if (rule.enabled === false) conditions.push('La regla está desactivada en la configuración.')

  const roll =
    rule.rollNumerator !== undefined && rule.rollDenominator
      ? `${fmt(rule.rollNumerator)} de ${fmt(rule.rollDenominator)} (${fmt(Math.round((rule.rollNumerator / rule.rollDenominator) * 10000) / 100)} %)`
      : undefined
  return { rule, roll, conditions, targets: Array.from(pairs.values()) }
}

/** Requisitos y objetivos de una misión para una clase (o generales sin clase). */
export function buildQuestView(w: WorldData, questId: number, cls?: CharacterClass): QuestView | undefined {
  const q = w.quests
  if (!q.quests.has(questId) && !q.rules.some((r) => r.questId === questId)) return undefined
  const code = cls ? classCodeOf(cls.id) : undefined
  const rules = q.rules.filter((r) => r.questId === questId)
  const normal = rules.filter((r) => r.stateName === 'normal' || r.state === 0)
  const starts = normal
    .filter((r) => appliesTo(q.ruleClasses.get(r.id), code))
    .map((r) => ({
      rule: r,
      classes: (q.ruleClasses.get(r.id) ?? []).filter((c) => !code || c.classCode === code),
      prereqTitle: r.requiredQuestId !== undefined ? questTitle(r.requiredQuestId) : undefined,
    }))
  const base = starts[0]?.rule ?? normal[0] ?? rules[0]

  const objs = q.objectives.filter((o) => o.questId === questId && appliesTo(q.objectiveClasses.get(o.id), code))
  const zenObjs = objs.filter((o) => o.typeName === 'zen' || o.typeName === 'money')
  const zenValues = Array.from(new Set(zenObjs.map((o) => o.quantity).filter((n): n is number => n !== undefined)))

  // Un mismo objetivo puede repetirse en estados alternativos (p. ej. normal y cancelada): se muestra una vez, sin sumar.
  const once = (key: (o: QuestObjective) => string) => {
    const seen = new Set<string>()
    return (o: QuestObjective) => {
      const k = key(o)
      if (seen.has(k)) return false
      seen.add(k)
      return true
    }
  }
  const classKey = (o: QuestObjective) => (q.objectiveClasses.get(o.id) ?? []).map((c) => `${c.classCode}${c.changeUpPlusOne ?? ''}`).sort().join(',')

  const items: QuestItemView[] = objs
    .filter((o) => o.typeName === 'item' && o.itemId)
    .filter(once((o) => `${o.itemId}@${o.itemLevel ?? ''}|${o.quantity ?? ''}|${classKey(o)}`))
    .map((o) => {
      const v = variantFor(w, o.itemId!, o.itemLevel)
      const rule = q.dropRules.find((d) => d.objectiveId === o.id)
      return {
        objective: o,
        itemId: o.itemId!,
        variantId: v.id,
        name: v.name,
        nameFromVariant: v.fromVariant,
        nameSource: v.nameSource,
        quantity: o.quantity,
        classes: q.objectiveClasses.get(o.id) ?? [],
        drop: rule ? dropView(w, rule, v.id) : undefined,
      }
    })

  const kills: QuestKillView[] = objs
    .filter((o) => o.typeName === 'monster_kills' || o.typeName === 'monster_kill' || o.typeName === 'kill')
    .filter(once((o) => `${o.monsterId ?? o.configuredMonsterId ?? ''}|${o.quantity ?? ''}`))
    .map((o) => {
      const id = o.monsterId ?? o.configuredMonsterId
      const m = id !== undefined ? w.monsters.get(id) : undefined
      const maps = Array.from(new Set((id !== undefined ? w.idx.spawnsByMonster.get(id) ?? [] : []).map((s) => s.mapId))).map((mid) => ({ id: mid, name: w.maps.get(mid)?.name ?? `Mapa ${mid}` }))
      return { objective: o, monsterId: m ? id : undefined, name: m?.name ?? `Monstruo ${id ?? '—'} (sin resolver)`, resolved: !!m && o.monsterId !== undefined, quantity: o.quantity, maps }
    })

  return {
    questId,
    title: questTitle(questId),
    npcMonsterId: base?.npcMonsterId,
    npcName: base?.npcMonsterId !== undefined ? w.monsters.get(base.npcMonsterId)?.name : undefined,
    levelMin: base?.levelMin,
    levelMax: base?.levelMax,
    starts,
    zen: zenValues.length === 1 ? zenValues[0] : undefined,
    zenStates: zenObjs.map((o) => o.stateName ?? String(o.state)),
    zenConsistent: zenValues.length <= 1,
    items,
    kills,
    eligible: !code || starts.length > 0,
  }
}

export const formatZen = (n: number) => `${fmt(n)} Zen`
